import { useDisclosure } from '#shared'
import { computed, reactive, watch, type Ref } from 'vue'
import type { AttachmentDto } from '#entities/message'
import type { ImageAttachment } from '#entities/message/model/attachment'
import type { ImageInfo } from '#shared/model/image-grid'
import { addAttachment } from '../api/add-attachment'

interface AttachmentsControllerOptions {
  maxAttachmentCount?: number
}

type UploadState = 'pending' | 'finished' | 'errored'

export function useAttachmentsController(
  attachments: Ref<AttachmentDto[]>,
  { maxAttachmentCount = 10 }: AttachmentsControllerOptions = {}
) {
  const onFileUploaded = async (file: File, url: string) => {
    const attachment = attachments.value.find(attachment => attachment.meta?.file === file)
    if (!attachment) return

    if (attachment.type === 'image' || attachment.type === 'file') {
      attachment.url = url
    }

    attachment.meta!.uploadState = 'finished'
  }

  const onFileUploadFailed = async (file: File) => {
    const attachment = attachments.value.find(attachment => attachment.meta?.file === file)
    if (!attachment) return

    attachment.meta!.uploadState = 'errored'
  }

  const uploadFile = async (file: File) => {
    let attachment = attachments.value.find(attachment => attachment.meta?.file === file)
    if (attachment && attachment.meta?.uploadState !== 'errored') return

    if (!attachment) {
      if (file.type.startsWith('image/')) {
        const imageInfo = await getImageInfo(file)
        const url = URL.createObjectURL(file)

        attachment = {
          clientId: crypto.randomUUID(),
          type: 'image',
          url,
          localUrl: url,
          width: imageInfo.width,
          height: imageInfo.height,
          meta: {
            file,
            uploadState: 'pending',
          },
        }

        attachments.value.push(attachment)
      } else {
        attachment = {
          type: 'file',
          url: URL.createObjectURL(file),
          name: file.name,
          meta: {
            file,
            uploadState: 'pending',
          },
        }

        attachments.value.push(attachment)
      }
    }

    try {
      const responseBody = await addAttachment(file)
      if (attachments.value.some(attachment => attachment.meta?.file === file))
        onFileUploaded(file, responseBody.data.url)
    } catch {
      if (attachments.value.some(attachment => attachment.meta?.file === file)) onFileUploadFailed(file)
    }
  }

  const getImageInfo = (file: File): Promise<ImageInfo> => {
    const fileObjectUrl = URL.createObjectURL(file)

    const imageEl = document.createElement('img')
    imageEl.src = fileObjectUrl
    imageEl.style.width = '1px'
    imageEl.style.height = '1px'
    imageEl.style.position = 'fixed'
    imageEl.style.top = '-1px'
    imageEl.style.left = '-1px'

    return new Promise((resolve, reject) => {
      imageEl.onload = () => {
        const width = imageEl.naturalWidth
        const height = imageEl.naturalHeight

        const imageInfo: ImageInfo = {
          file,
          src: fileObjectUrl,
          alt: file.name,
          width,
          height,
        }

        document.body.removeChild(imageEl)
        resolve(imageInfo)
      }

      imageEl.onerror = () => {
        document.body.removeChild(imageEl)
        reject(new Error('Failed to get image info'))
      }

      document.body.appendChild(imageEl)
    })
  }

  const addFile = (file: File) => {
    if (attachments.value.length >= maxAttachmentCount) return

    attachmentsModalDisclosure.open()

    uploadFile(file)

    if (file.type.startsWith('image/')) {
      getImageInfo(file)
    }
  }

  const fileInput = document.createElement('input')
  fileInput.type = 'file'
  fileInput.multiple = true

  const showFileDialog = () => {
    fileInput.click()
  }

  fileInput.addEventListener('change', () => {
    const files = fileInput.files
    if (!files) return

    for (const file of Array.from(files)) {
      addFile(file)
    }

    fileInput.value = ''
  })

  const attachmentsModalDisclosure = useDisclosure()

  const reset = () => {
    attachments.value = []
  }

  watch(attachments, attachments => {
    if (!attachments.length) attachmentsModalDisclosure.close()
  })

  const uploadState = computed((): UploadState => {
    let hasErroredFile = false
    let hasPendingFile = false

    for (const attachment of attachments.value) {
      if (attachment.meta?.uploadState === 'errored') hasErroredFile = true
      if (attachment.meta?.uploadState === 'pending') hasPendingFile = true
    }

    if (hasErroredFile) return 'errored'
    if (hasPendingFile) return 'pending'
    return 'finished'
  })

  const imageAttachments = computed(
    () => attachments.value.filter(attachment => attachment.type === 'image') as ImageAttachment[]
  )

  const fileAttachments = computed(() => attachments.value.filter(attachment => attachment.type === 'file'))

  const imageInfos = computed<ImageInfo<ImageAttachment>[]>(() =>
    imageAttachments.value.map(attachment => ({
      src: attachment.localUrl ?? import.meta.env.VITE_BASE_API_URL + attachment.url,
      alt: 'Attachement',
      width: attachment.width,
      height: attachment.height,
      file: attachment.meta?.file,
      meta: attachment,
    }))
  )

  const deleteAttachment = (deletingAttachment: AttachmentDto) => {
    attachments.value = attachments.value.filter(
      attachment =>
        (!attachment.clientId || attachment.clientId !== deletingAttachment.clientId) &&
        (!attachment.id || attachment.id !== deletingAttachment.id)
    )
  }

  return reactive({
    showFileDialog,
    attachmentsModalDisclosure,
    reset,
    uploadState,
    imageAttachments,
    imageInfos,
    fileAttachments,

    deleteAttachment,

    uploadFile,
  })
}

export type AttachmentsController = ReturnType<typeof useAttachmentsController>
